import manifest from '../generated/image-manifest.json';
import index from '../content/galleries.json';

// Everything is discovered from folders, so adding a gallery (e.g. from the
// CMS) never needs a code change:
//   src/content/galleries/{slug}.json   — the gallery's text, kind, captions
//   src/content/galleries.json          — display order
//   src/assets/photos/{slug}/*.jpg      — its photos ("home" = selected work)
const contentCtx = require.context('../content/galleries', false, /\.json$/);
const photoCtx = require.context('../assets/photos', true, /^\.\/[a-z0-9-]+\/[^/]+\.(jpe?g|png)$/i);
const webpCtx = require.context('../assets/photos', true, /^\.\/[a-z0-9-]+\/[^/]+\.webp$/);

const folderOf = (key) => key.split('/')[1];
const fileOf = (key) => key.split('/').slice(2).join('/');

const stemOf = (file) => file.replace(/\.(jpe?g|png|webp)$/i, '').replace(/-(800|1600)w$/, '');

const webpByFolder = new Map();
for (const key of webpCtx.keys()) {
  const folder = folderOf(key);
  const base = fileOf(key).replace(/\.webp$/, '');
  const size = base.match(/-(800|1600)w$/);
  const stem = base.replace(/-(800|1600)w$/, '');
  if (!webpByFolder.has(folder)) webpByFolder.set(folder, new Map());
  const map = webpByFolder.get(folder);
  if (!map.has(stem)) map.set(stem, {});
  const entry = map.get(stem);
  if (!size) entry.full = webpCtx(key);
  else if (size[1] === '800') entry.w800 = webpCtx(key);
  else entry.w1600 = webpCtx(key);
}

const photoKeysByFolder = new Map();
for (const key of photoCtx.keys()) {
  const folder = folderOf(key);
  if (!photoKeysByFolder.has(folder)) photoKeysByFolder.set(folder, []);
  photoKeysByFolder.get(folder).push(key);
}

const contentBySlug = new Map(
  contentCtx.keys().map((key) => [key.replace(/^\.\//, '').replace(/\.json$/, ''), contentCtx(key)])
);

function firstSentence(text = '') {
  const match = text.match(/^.*?[.!?](\s|$)/);
  return (match ? match[0] : text).trim();
}

const cache = new Map();

/**
 * All images in a photo folder, in stable (filename) order:
 *   { slug, stem, src, webp, webp800, webp1600, width, height, aspect, color, caption }
 * Callers shuffle as needed. Images missing from the manifest get a 3:2
 * fallback so a stale manifest degrades gracefully instead of breaking.
 */
export const imagesFor = (slug) => {
  if (cache.has(slug)) return cache.get(slug);
  const keys = photoKeysByFolder.get(slug) || [];
  const webp = webpByFolder.get(slug) || new Map();
  const dims = manifest[slug] || {};
  const captions = contentBySlug.get(slug)?.captions || {};

  const images = keys.map((key) => {
    const stem = stemOf(fileOf(key));
    const [width = 1500, height = 1000, color = '#ede6d8'] = dims[stem] || [];
    const v = webp.get(stem) || {};
    return {
      slug,
      stem,
      src: photoCtx(key),
      webp: v.full || null,
      webp800: v.w800 || null,
      webp1600: v.w1600 || null,
      width,
      height,
      aspect: width / height,
      color,
      caption: captions[stem] || '',
    };
  });
  cache.set(slug, images);
  return images;
};

// Published galleries with at least one photo, in the CMS-controlled order.
// This drives routes, the home carousel, the work index, the drawer, search,
// and "next gallery" links.
export const GALLERIES = index.order
  .filter((slug) => contentBySlug.has(slug))
  .map((slug) => {
    const content = contentBySlug.get(slug);
    return {
      slug,
      kind: content.kind === 'trip' ? 'trip' : 'gallery',
      content,
      path: content.path,
      title: content.title,
      blurb: content.blurb || firstSentence(content.intro),
      cover: content.cover || '',
    };
  })
  .filter((g) => g.content.published !== false && imagesFor(g.slug).length > 0);

export const KIND_LABEL = { gallery: 'Gallery', trip: 'Trip' };

/** Shuffle-friendly helper: the gallery's chosen cover (if any) moved to the front. */
export const coverFirst = (images, stem) => {
  const i = stem ? images.findIndex((img) => img.stem === stem) : -1;
  return i > 0 ? [images[i], ...images.slice(0, i), ...images.slice(i + 1)] : images;
};

export const galleryBySlug = (slug) => GALLERIES.find((g) => g.slug === slug);
export const galleryByPath = (path) => GALLERIES.find((g) => g.path === path);

export const nextGallery = (slug) => {
  const i = GALLERIES.findIndex((g) => g.slug === slug);
  return GALLERIES[(i + 1) % GALLERIES.length];
};

export const isPortrait = (img) => img.aspect < 0.95;
export const isLandscape = (img) => img.aspect > 1.05;
